import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Gauge, Sparkles, Loader2, Pencil, TrendingUp, Euro, Target, CheckCircle2 } from "lucide-react";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const TYPE_COLORS = {
  initiative: "#007934",
  pilot: "#0ea5e9",
  rd_project: "#8b5cf6",
  challenge: "#f59e0b",
};
const TYPE_LABELS = {
  initiative: "Iniciativa",
  pilot: "Piloto",
  rd_project: "Proyecto I+D",
  challenge: "Reto NOVA",
};

export default function XVPage() {
  const { token } = useAuth();
  const headers = useCallback(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [onlyScored, setOnlyScored] = useState(false);
  const [search, setSearch] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [ri, rs] = await Promise.all([
        fetch(`${API}/xv/items`, { headers: headers() }),
        fetch(`${API}/xv/summary`, { headers: headers() }),
      ]);
      const di = await ri.json();
      const ds = await rs.json();
      setItems(di.items || []);
      setSummary(ds);
    } catch {
      toast.error("Error cargando datos xV");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((it) => {
    if (typeFilter !== "all" && it.item_type !== typeFilter) return false;
    if (onlyScored && !it.score) return false;
    if (search && !it.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => (b.score?.xv || 0) - (a.score?.xv || 0));
  const scatterData = items
    .filter((it) => it.score && it.score.xv > 0)
    .map((it) => ({
      name: it.name, type: it.item_type,
      probability: it.score.probability,
      value: it.score.potential_value,
      xv: it.score.xv,
    }));

  return (
    <div className="space-y-6" data-testid="xv-page">
      {/* Hero */}
      <div className="rounded-2xl p-6 md:p-8 text-white relative overflow-hidden" style={{ background: "linear-gradient(120deg, #0a3d22, #007934 60%, #3BAD2D)" }}>
        <div className="relative z-10">
          <div className="flex items-center gap-2 text-emerald-200 text-xs font-semibold uppercase tracking-widest mb-2">
            <Gauge className="w-4 h-4" /> Decision Intelligence
          </div>
          <h1 className="text-3xl md:text-4xl font-black">xV · Valor Esperado</h1>
          <p className="text-emerald-100/90 mt-2 max-w-2xl text-sm">
            Una métrica común para todas las iniciativas: <strong>xV = Valor potencial × Probabilidad × Confianza</strong>.
            Prioriza bajo incertidumbre y compara retos, pilotos y proyectos con la misma vara.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: "xV Total", value: fmtEur(summary.total_xv), icon: TrendingUp, color: "text-emerald-600" },
            { label: "Valor Potencial", value: fmtEur(summary.total_potential), icon: Euro, color: "text-sky-600" },
            { label: "Valor Realizado", value: fmtEur(summary.total_realized), icon: CheckCircle2, color: "text-violet-600" },
            { label: "Tasa Realización", value: `${summary.realization_rate}%`, icon: Target, color: "text-amber-600" },
            { label: "Elementos valorados", value: summary.scored_count, icon: Gauge, color: "text-emerald-600" },
          ].map((c, i) => (
            <Card key={i} data-testid={`xv-stat-${i}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                  <c.icon className={`w-3.5 h-3.5 ${c.color}`} /> {c.label}
                </div>
                <p className="text-xl font-bold">{c.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Prioritization matrix */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-bold text-base">Matriz de Priorización</h2>
              <p className="text-xs text-muted-foreground">Probabilidad de éxito vs Valor potencial · tamaño = xV</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: TYPE_COLORS[k] }} /> {v}
                </span>
              ))}
            </div>
          </div>
          {scatterData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-sm text-muted-foreground">
              Valora elementos para verlos en la matriz
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" dataKey="probability" name="Probabilidad" unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="number" dataKey="value" name="Valor potencial" tickFormatter={(v) => `${Math.round(v / 1000)}k€`} tick={{ fontSize: 11 }} width={55} />
                <ZAxis type="number" dataKey="xv" range={[80, 600]} />
                <RTooltip
                  cursor={{ strokeDasharray: "3 3" }}
                  content={({ payload }) => {
                    if (!payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-background border rounded-lg shadow-lg p-3 text-xs">
                        <p className="font-semibold mb-1">{d.name}</p>
                        <p>Probabilidad: {d.probability}%</p>
                        <p>Valor potencial: {fmtEur(d.value)}</p>
                        <p className="font-bold text-emerald-600">xV: {fmtEur(d.xv)}</p>
                      </div>
                    );
                  }}
                />
                <Scatter data={scatterData}>
                  {scatterData.map((d, i) => (
                    <Cell key={i} fill={TYPE_COLORS[d.type] || "#007934"} fillOpacity={0.75} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Filters + ranking */}
      <Card>
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
            <h2 className="font-bold text-base flex-1">Ranking xV</h2>
            <Input placeholder="Buscar..." value={search} onChange={(e) => setSearch(e.target.value)} className="md:w-52" data-testid="xv-search" />
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="md:w-44" data-testid="xv-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={onlyScored} onCheckedChange={setOnlyScored} data-testid="xv-only-scored" /> Solo valorados
            </label>
          </div>

          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : (
            <div className="space-y-1.5 max-h-[560px] overflow-y-auto pr-1">
              {sorted.slice(0, 120).map((it) => (
                <div
                  key={`${it.item_type}-${it.item_id}`}
                  className="flex items-center gap-3 p-3 rounded-lg border hover:border-emerald-500/40 transition-colors"
                  data-testid={`xv-item-${it.item_id}`}
                >
                  <span className="w-2 h-8 rounded-full flex-shrink-0" style={{ background: TYPE_COLORS[it.item_type] }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{it.name}</p>
                    <p className="text-[11px] text-muted-foreground">{it.item_type_label}</p>
                  </div>
                  {it.score ? (
                    <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{fmtEur(it.score.potential_value)}</span>
                      <span>{it.score.probability}% prob.</span>
                      <span>{it.score.confidence}% conf.</span>
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Sin valorar</Badge>
                  )}
                  {it.score && (
                    <span className="font-bold text-sm text-emerald-600 w-24 text-right">{fmtEur(it.score.xv)}</span>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditItem(it)} data-testid={`xv-edit-${it.item_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              {sorted.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Sin resultados</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {editItem && (
        <ScoreDialog
          item={editItem}
          headers={headers}
          onClose={() => setEditItem(null)}
          onSaved={() => { setEditItem(null); load(); }}
        />
      )}
    </div>
  );
}

function ScoreDialog({ item, headers, onClose, onSaved }) {
  const s = item.score || {};
  const [potentialValue, setPotentialValue] = useState(s.potential_value || 0);
  const [probability, setProbability] = useState(s.probability ?? 50);
  const [confidence, setConfidence] = useState(s.confidence ?? 50);
  const [realizedValue, setRealizedValue] = useState(s.realized_value || 0);
  const [notes, setNotes] = useState(s.notes || "");
  const [rationale, setRationale] = useState(s.rationale || "");
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const xv = potentialValue * (probability / 100) * (confidence / 100);

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const r = await fetch(`${API}/xv/ai-suggest`, {
        method: "POST", headers: headers(),
        body: JSON.stringify({ item_type: item.item_type, item_id: item.item_id }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      setPotentialValue(d.potential_value);
      setProbability(d.probability);
      setConfidence(d.confidence);
      setRationale(d.rationale);
      toast.success("Sugerencia IA aplicada");
    } catch (e) {
      toast.error(`Error IA: ${e.message}`);
    } finally {
      setSuggesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/xv/score`, {
        method: "PUT", headers: headers(),
        body: JSON.stringify({
          item_type: item.item_type, item_id: item.item_id, item_name: item.name,
          potential_value: potentialValue, probability, confidence,
          realized_value: realizedValue, notes, rationale,
        }),
      });
      if (!r.ok) throw new Error("Error guardando");
      toast.success("Valoración xV guardada");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="xv-score-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Gauge className="w-5 h-5 text-emerald-600" /> Valorar: {item.name}</DialogTitle>
          <DialogDescription>{item.item_type_label} · xV = Valor × Probabilidad × Confianza</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={aiSuggest} disabled={suggesting} className="w-full" data-testid="xv-ai-suggest">
            {suggesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2 text-emerald-600" />}
            Sugerir valoración con IA
          </Button>
          {rationale && (
            <p className="text-xs text-muted-foreground bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2.5">{rationale}</p>
          )}
          <div>
            <label className="text-xs font-medium">Valor potencial anual (€)</label>
            <Input type="number" value={potentialValue} onChange={(e) => setPotentialValue(parseFloat(e.target.value) || 0)} data-testid="xv-input-value" />
          </div>
          <div>
            <label className="text-xs font-medium flex justify-between"><span>Probabilidad de éxito</span><span className="font-bold">{probability}%</span></label>
            <Slider value={[probability]} onValueChange={([v]) => setProbability(v)} max={100} step={5} className="mt-2" data-testid="xv-slider-prob" />
          </div>
          <div>
            <label className="text-xs font-medium flex justify-between"><span>Confianza en la estimación</span><span className="font-bold">{confidence}%</span></label>
            <Slider value={[confidence]} onValueChange={([v]) => setConfidence(v)} max={100} step={5} className="mt-2" data-testid="xv-slider-conf" />
          </div>
          <div>
            <label className="text-xs font-medium">Valor realizado hasta hoy (€)</label>
            <Input type="number" value={realizedValue} onChange={(e) => setRealizedValue(parseFloat(e.target.value) || 0)} data-testid="xv-input-realized" />
          </div>
          <Textarea placeholder="Notas (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          <div className="flex items-center justify-between p-3 rounded-xl text-white" style={{ background: "linear-gradient(135deg, #007934, #3BAD2D)" }}>
            <span className="text-sm font-medium">Valor Esperado (xV)</span>
            <span className="text-xl font-black" data-testid="xv-computed">{fmtEur(xv)}</span>
          </div>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="xv-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Guardar valoración
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
